/**
 * Module-Specific Error Boundary
 * Provides customized error pages for different application modules
 */

import React from 'react';
import {
  useNavigate,
  useRouteError,
  isRouteErrorResponse,
} from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ModuleErrorProps {
  moduleName: string;
  moduleIcon?: React.ReactNode;
  accentColor?: string;
  supportText?: string;
}

/**
 * Base error page component for modules
 */
export const ModuleErrorPage: React.FC<ModuleErrorProps> = ({
  moduleName,
  moduleIcon,
  accentColor = 'blue',
  supportText,
}) => {
  const error = useRouteError();
  const navigate = useNavigate();

  // Determine error type and message
  let errorTitle = 'Something went wrong';
  let errorMessage = 'An unexpected error occurred while loading this page.';
  let errorCode: number | undefined;

  if (isRouteErrorResponse(error)) {
    errorCode = error.status;
    if (error.status === 404) {
      errorTitle = 'Page not found';
      errorMessage = `The ${moduleName.toLowerCase()} page you're looking for doesn't exist or has been moved.`;
    } else if (error.status === 403) {
      errorTitle = 'Access denied';
      errorMessage = `You don't have permission to access this ${moduleName.toLowerCase()} page.`;
    } else if (error.status === 500) {
      errorTitle = 'Server error';
      errorMessage = `We're having trouble loading ${moduleName.toLowerCase()} data. Please try again later.`;
    }
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    red: 'bg-red-100 text-red-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  const buttonColorClasses = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    red: 'bg-red-600 hover:bg-red-700',
    green: 'bg-green-600 hover:bg-green-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    orange: 'bg-orange-600 hover:bg-orange-700',
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white shadow-lg rounded-xl p-8 text-center">
        {/* Error Icon */}
        <div
          className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 ${colorClasses[accentColor as keyof typeof colorClasses] || colorClasses.blue}`}
        >
          {moduleIcon || <AlertTriangle className="w-8 h-8" />}
        </div>

        {/* Error Code Badge */}
        {errorCode && (
          <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium mb-4">
            Error {errorCode}
          </span>
        )}

        {/* Error Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{errorTitle}</h1>

        {/* Module Name */}
        <p className="text-sm text-gray-500 mb-4">{moduleName}</p>

        {/* Error Message */}
        <p className="text-gray-600 mb-6">{errorMessage}</p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => window.location.reload()}
            className={`${buttonColorClasses[accentColor as keyof typeof buttonColorClasses] || buttonColorClasses.blue} text-white`}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              void navigate(-1);
            }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              void navigate('/dashboard');
            }}
          >
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
        </div>

        {/* Support Text */}
        {supportText && (
          <p className="mt-6 text-sm text-gray-500">{supportText}</p>
        )}

        {/* Dev Error Details */}
        {import.meta.env.DEV && error instanceof Error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
              Technical Details
            </summary>
            <pre className="mt-2 text-xs bg-gray-50 p-3 rounded-lg overflow-auto text-red-600">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

// Pre-configured error pages for each module
export const DashboardError = () => (
  <ModuleErrorPage
    moduleName="Dashboard"
    accentColor="blue"
    supportText="If this problem persists, please contact support."
  />
);

export const OrdersError = () => (
  <ModuleErrorPage
    moduleName="Orders"
    accentColor="green"
    supportText="Your orders are safe. Please try again."
  />
);

export const ContentOrdersError = () => (
  <ModuleErrorPage
    moduleName="Content Orders"
    accentColor="orange"
    supportText="Your content orders are safe. Please try again."
  />
);

export const WalletError = () => (
  <ModuleErrorPage
    moduleName="Wallet"
    accentColor="green"
    supportText="Your funds are safe. Please try refreshing."
  />
);

export const ProfileError = () => (
  <ModuleErrorPage
    moduleName="Profile"
    accentColor="blue"
    supportText="Your profile data is safe. Please try again."
  />
);

export const HelpCenterError = () => (
  <ModuleErrorPage
    moduleName="Help Center"
    accentColor="blue"
    supportText="Need immediate help? Contact support@domainboost.com"
  />
);

export const PackagesError = () => (
  <ModuleErrorPage
    moduleName="Packages"
    accentColor="purple"
    supportText="Your subscription is safe. Please try again."
  />
);

export const BrowseSitesError = () => (
  <ModuleErrorPage
    moduleName="Browse Sites"
    accentColor="blue"
    supportText="Try refreshing to see available sites."
  />
);

export const NotificationsError = () => (
  <ModuleErrorPage
    moduleName="Notifications"
    accentColor="blue"
    supportText="Your notification settings are safe. Please try again."
  />
);

export const TeamError = () => (
  <ModuleErrorPage
    moduleName="Team"
    accentColor="purple"
    supportText="Your team data is safe. Please try again."
  />
);

export const SubscriptionsError = () => (
  <ModuleErrorPage
    moduleName="Subscriptions"
    accentColor="blue"
    supportText="Your subscription is safe. Please try again."
  />
);
